import React, { useState, useMemo } from "react";
import TopFields, {
  FieldsConfig,
  TopFieldsContext
} from "@/components/layouts/TopFields";
import { disqualifyModalMode, LeadData, LeadStatus } from "@/pages/leads/types";
import { useUsers } from "@/entities/users/hooks";
import { useUserSelect } from "@/entities/users/useUserSelect";
import {
  getLicenseTypesForLead,
  leadStatusColors
} from "@/pages/leads/helpers";
import Text from "@/components/ui/text";
import { useTranslation } from "react-i18next";
import { getEnumTKey } from "@/utils/strings";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from "@/components/ui/tooltip";
import DetailsPopover from "@/components/controls/DetailsPopover";
import { useClosedAsQualifiedReasons, useDisqualifiedReasons } from "../hooks";
import {
  closedAsQualifiedReasonsFromIdx,
  disqualifiedReasonsFromIdx
} from "@/pages/leads/helpers";
import LicenseFieldsEditor from "@/components/controls/LicenseFieldsEditor";
import { EntityType } from "@/services/hooks/entities/useEntityType";
import { useEntitySelect } from "@/services/hooks/entities/useEntitySelect";
import { useProducts } from "@/entities/settings/hooks";
import IconContainer from "@/components/ui/icon-container";
import { Bullet } from "@dreamhub/designsystem/icons";
import UserAvatar from "@/components/controls/UserAvatar";
import { get, isEmpty, map } from "lodash";
import { useActiveFieldKeys, useEntityFields } from "@/pages/settings/hooks";
import { FieldDisplayType } from "@/pages/settings/components/dealsSettings/FieldManagement/types";
import EntitySelectField from "@/components/controls/EntitySelectField";
import { useCompanies } from "@/pages/companies/hooks";
import { useEnablePartnerships } from "@/pages/partners/hooks";

interface Props {
  lead?: LeadData;
  onUpdateField: (
    field: string,
    newValue: string | number,
    additionalFields?: { [fieldName: string]: string | number }
  ) => void;
  handleUnmarkAsStuck: () => void;
  handleOpenDisqualifyLeadModal: (
    mode: disqualifyModalMode,
    selectedReason?: number | null,
    notesInput?: string | null,
    selectedCompanyReason?: number | null
  ) => void;
  licenseTypes: { id: string; name: string; productId: string }[];
  className?: string;
  itemClassName?: string;
  context?: TopFieldsContext;
}

const LeadTopFields: React.FC<Props> = ({
  lead,
  onUpdateField,
  handleUnmarkAsStuck,
  handleOpenDisqualifyLeadModal,
  licenseTypes,
  className,
  itemClassName,
  context
}) => {
  const { t } = useTranslation();
  const {
    activeFieldKeys,
    detailViewFieldKeys,
    isLoaded: fieldsLoaded
  } = useActiveFieldKeys(EntityType.LEAD);
  const {
    options: userOptions,
    entities: userEntities,
    isLoading: isLoadingUsers,
    searchTerm: userSearchTerm,
    setSearchTerm: setUserSearchTerm
  } = useUserSelect();
  const {
    users: [changedByUser, sdrOwnerUser]
  } = useUsers([lead?.statusChangedBy || "", lead?.sdrOwner || ""]);

  const products = useProducts().data?.products ?? [];

  const { data: leadFields } = useEntityFields(EntityType.LEAD);
  const enablePartnerships = useEnablePartnerships();
  const partnerFieldKey = enablePartnerships
    ? leadFields?.fields?.find(
        (field) => field.displayType === FieldDisplayType.PARTNERS
      )?.fieldKey
    : undefined;
  const partnerContactFieldKey = leadFields?.fields?.find(
    (field) => field.displayType === FieldDisplayType.PARTNER_CONTACTS
  )?.fieldKey;

  const closedLeadReasons =
    (lead?.status === LeadStatus.DISQUALIFIED
      ? useDisqualifiedReasons().data?.disqualifiedReasons
      : useClosedAsQualifiedReasons().data?.closedAsQualifiedReasons) ?? [];

  const {
    entities: [companyData]
  } = useCompanies([lead?.companyId || ""]);

  const contactFilter = useMemo(() => {
    if (!lead?.companyId) return {};
    const companyIds = [lead.companyId];
    if (companyData?.parentCompany) {
      companyIds.push(companyData.parentCompany);
    }
    return companyIds.length > 1
      ? { companyId: { in: companyIds } }
      : { companyId: lead.companyId };
  }, [lead?.companyId, companyData?.parentCompany]);

  const leadLicenseTypes = useMemo(
    () => (lead ? getLicenseTypesForLead(licenseTypes, lead) : []),
    [licenseTypes, lead]
  );

  const {
    options: companyOptions,
    onSearch: onCompanyOptionsSearch,
    isLoading: isLoadingCompanies,
    searchTerm: companySearchTerm
  } = useEntitySelect({
    entityType: EntityType.COMPANY,
    selectedId: lead?.companyId || "",
    alwaysEnabled: true
  });

  const {
    options: contactOptions,
    isLoading: isLoadingContacts,
    onSearch: onContactSearch,
    searchTerm: contactSearchTerm
  } = useEntitySelect({
    entityType: EntityType.PERSON,
    selectedId: lead?.contactId || "",
    filters: contactFilter
  });

  const [isPopoverOpen, setIsPopoverOpen] = useState(false);

  const handleUpdateLicenseFields = async (
    licenseId: string | null,
    numberOfLicenses: number
  ) => {
    const currentLicenseId = lead?.licenseId || "";
    const currentNumberOfLicenses = lead?.numberOfLicenses || 0;

    const licenseIdChanged = (licenseId || "") !== currentLicenseId;
    const numberOfLicensesChanged =
      numberOfLicenses !== currentNumberOfLicenses;

    if (licenseIdChanged && !numberOfLicensesChanged) {
      onUpdateField("licenseId", licenseId || "", {
        numberOfLicenses: numberOfLicenses
      });
    } else if (!licenseIdChanged && numberOfLicensesChanged) {
      onUpdateField("numberOfLicenses", numberOfLicenses, {
        licenseId: licenseId || ""
      });
    } else {
      onUpdateField("numberOfLicenses", numberOfLicenses, {
        licenseId: licenseId || ""
      });
    }
  };

  const handleUpdateProductField = async (productId: string) => {
    onUpdateField("productId", productId, {
      licenseId: null as unknown as string
    });
  };

  const renderStatus = () => (
    <div className="flex items-center gap-1 pr-2">
      <IconContainer
        style={{ color: leadStatusColors[lead?.status as LeadStatus] }}
      >
        <Bullet />
      </IconContainer>
      <Text variant="body">
        {t(
          getEnumTKey(
            "leads",
            "statusValues",
            LeadStatus,
            lead?.status as number
          )
        )}
      </Text>
    </div>
  );

  const renderDetailedStatus = (
    status:
      | LeadStatus.DISQUALIFIED
      | LeadStatus.STUCK
      | LeadStatus.CLOSED_QUALIFIED
  ) => {
    const isStuck = status === LeadStatus.STUCK;

    const handleEdit = () => {
      if (isStuck) {
        return handleUnmarkAsStuck();
      }
      handleOpenDisqualifyLeadModal(
        status === LeadStatus.DISQUALIFIED
          ? "disqualified"
          : "closedAsQualified",
        status === LeadStatus.DISQUALIFIED
          ? lead?.disqualifiedReason
          : lead?.closedAsQualifiedReason,
        lead?.statusNotes,
        lead?.disqualifiedCompanyReason
      );
    };

    const getStatusReasonProps = () => {
      if (isStuck || !closedLeadReasons?.length) {
        return {};
      }
      const reason =
        status === LeadStatus.DISQUALIFIED && lead?.disqualifiedReason
          ? disqualifiedReasonsFromIdx(
              t,
              closedLeadReasons,
              lead?.disqualifiedReason
            )
          : status === LeadStatus.CLOSED_QUALIFIED &&
              lead?.closedAsQualifiedReason
            ? closedAsQualifiedReasonsFromIdx(
                t,
                closedLeadReasons,
                lead?.closedAsQualifiedReason
              )
            : "";
      if (!isEmpty(reason)) {
        return {
          statusReasonTitle: t(
            `leads.leadView.reasonTitle.${status === LeadStatus.DISQUALIFIED ? "disqualified" : "closedAsQualified"}`
          ),
          statusReasonText: reason
        };
      }
    };
    return (
      <div className="flex gap-[2px] items-center pr-2">
        {renderStatus()}
        <DetailsPopover
          isOpen={isPopoverOpen}
          setIsOpen={setIsPopoverOpen}
          onEdit={handleEdit}
          editText={isStuck ? t("leads.leadView.unmarkAsStuck") : undefined}
          {...getStatusReasonProps()}
          statusNotesTitle={
            isStuck
              ? t("leads.leadView.stuckNotesTitle")
              : t("leads.leadView.noteTitle")
          }
          statusNotes={lead?.statusNotes}
          statusChangedAt={lead?.statusChangedAt}
          changedByUser={changedByUser}
        />
      </div>
    );
  };

  const fieldsConfig: FieldsConfig = {
    companyId: {
      type: "select",
      icon: "company",
      entity: EntityType.COMPANY,
      options: companyOptions,
      searchTerm: companySearchTerm,
      showSearch: true,
      onSearch: onCompanyOptionsSearch,
      isLoading: isLoadingCompanies,
      popoverClassName: "max-w-[30vw]"
    },
    contactId: {
      type: "select",
      icon: "user",
      disabled: isEmpty(lead?.companyId),
      disabledTooltipText: t("leads.leadView.disabledContactId"),
      options: contactOptions,
      entity: EntityType.PERSON,
      showSearch: true,
      popoverClassName: "max-w-[30vw]",
      searchTerm: contactSearchTerm,
      onSearch: onContactSearch,
      isLoading: isLoadingContacts,
      isEntityField: true,
      displayType: "people"
    },
    ...((!fieldsLoaded || activeFieldKeys.has("productId")) && {
      productId: {
        type: "select" as const,
        options: map(products, (product) => ({
          id: product.id,
          displayName: product.name
        })),
        icon: "product" as const,
        onSelect: (value: string) => handleUpdateProductField(value)
      }
    }),
    ...((!fieldsLoaded || activeFieldKeys.has("sdrOwner")) && {
      sdrOwner: {
        type: "select" as const,
        entity: EntityType.USER,
        options: userOptions,
        showEmptyIcon: true,
        showSearch: true,
        searchTerm: userSearchTerm,
        onSearch: setUserSearchTerm,
        isLoading: isLoadingUsers,
        renderOptionIcon: (value: string | number) => {
          const matchingUser =
            userEntities.find(
              (userItem) => userItem.id === (value as string)
            ) ??
            (sdrOwnerUser?.id === (value as string) ? sdrOwnerUser : undefined);
          return (
            <UserAvatar userData={matchingUser} size="S" className="mr-1" />
          );
        }
      }
    }),
    ...(partnerFieldKey
      ? {
          [partnerFieldKey]: {
            type: "nonEditable" as const,
            icon: "partner" as const,
            customComponent: (
              <EntitySelectField
                fieldName={partnerFieldKey}
                displayType={FieldDisplayType.PARTNERS}
                value={(get(lead, partnerFieldKey) as string) ?? null}
                onChange={(newValue) =>
                  onUpdateField(
                    partnerFieldKey,
                    newValue as string,
                    partnerContactFieldKey
                      ? { [partnerContactFieldKey]: null as unknown as string }
                      : undefined
                  )
                }
              />
            )
          }
        }
      : {}),
    status: {
      type: "nonEditable",
      customComponent:
        lead?.status === LeadStatus.DISQUALIFIED ||
        lead?.status === LeadStatus.STUCK ||
        lead?.status === LeadStatus.CLOSED_QUALIFIED ? (
          renderDetailedStatus(lead?.status)
        ) : (
          <Tooltip>
            <TooltipTrigger>{renderStatus()}</TooltipTrigger>
            <TooltipContent className="max-w-56" sideOffset={4}>
              <div className="flex gap-1">
                {t(
                  getEnumTKey(
                    "leads",
                    "statusesTooltips",
                    LeadStatus,
                    lead?.status as number
                  )
                )}
                {/* TODO: uncomment when there's a related entity- for example, converted to a certain deal */}
                {/* {relatedEntity && (
                  <Text
                    variant="smLink"
                    className="cursor-pointer"
                    onClick={relatedEntityType ? openDetailedView : undefined}
                  >
                    {relatedEntity?.name}
                  </Text>
                )} */}
              </div>
            </TooltipContent>
          </Tooltip>
        )
    },
    ...((!fieldsLoaded || detailViewFieldKeys.has("licenseId")) && {
      licenseId: {
        type: "nonEditable" as const,
        customComponent: lead && (
          <LicenseFieldsEditor
            lead={lead}
            onUpdateFields={handleUpdateLicenseFields}
            // The backend rejects license fields on a lead without a product
            disabled={!lead.productId}
            disabledTooltipText={t("leads.leadView.disabledLicenseId")}
            licenseTypes={leadLicenseTypes}
          />
        )
      }
    })
  };

  return (
    <TopFields
      entity={lead}
      fieldsConfig={fieldsConfig}
      onUpdateField={onUpdateField}
      className={className}
      itemClassName={itemClassName}
      context={context}
    />
  );
};

export default LeadTopFields;
